import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Button } from '@mdvip/shared/components/ui/button';
import { Skeleton } from '@mdvip/shared/components/ui/skeleton';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DatasetState } from '@/hooks/member-views/useMemberDataset';

export interface StateCopy {
  title: string;
  description?: string;
}

export interface StateBoundaryProps {
  state: DatasetState;
  onRetry: () => void;
  /** Replaces the default "no patients" copy, e.g. when a letter or search matches nothing. */
  empty?: StateCopy;
  /** The loaded content. */
  children: ReactNode;
}

/** How many placeholder cards stand in for the list while it loads. */
const SKELETON_CARDS = 3;

/**
 * The copy each non-loaded state shows. The table layout renders these as table rows and the card
 * layout as cards, so both read from here to stay word-for-word the same.
 */
export function useStateCopy(empty?: StateCopy) {
  const { t } = useTranslation('providers');

  return {
    loadingLabel: t('memberViews.states.loadingLabel'),
    error: { title: t('memberViews.states.error.title'), description: t('memberViews.states.error.body') },
    empty: empty ?? { title: t('memberViews.states.empty.title'), description: t('memberViews.states.empty.body') },
  };
}

export function RetryButton({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('providers');

  return (
    <Button type="button" variant="outline" size="sm" onClick={onRetry}>
      <Icon name="refreshCw" size="xs" />
      {t('memberViews.states.error.retry')}
    </Button>
  );
}

/** One full-width message card, standing in for the list. */
function StateCard({ copy, action, role }: { copy: StateCopy; action?: ReactNode; role?: 'alert' }) {
  return (
    <div role={role} className="rounded-2xl border bg-card p-6 text-center">
      <p className="text-sm font-semibold text-foreground">{copy.title}</p>
      {copy.description && <p className="mt-1 text-sm text-muted-foreground">{copy.description}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}

/**
 * StateBoundary — the card layout's loading, error and empty states, and its content once loaded.
 *
 * A view that never loaded shows the error with a retry; a retry in progress shows the loading
 * state again. Which state applies is the dataset hook's call — this only renders it.
 */
export function StateBoundary({ state, onRetry, empty, children }: StateBoundaryProps) {
  const copy = useStateCopy(empty);

  if (state === 'loading') {
    return (
      <div className="flex flex-col gap-2">
        <span role="status" className="sr-only">
          {copy.loadingLabel}
        </span>
        {Array.from({ length: SKELETON_CARDS }, (_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: placeholder cards have no identity and never reorder.
          <div key={i} aria-hidden="true" className="rounded-2xl border bg-card p-4">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-2 h-3 w-56" />
            <Skeleton className="mt-4 h-9 w-full" />
          </div>
        ))}
      </div>
    );
  }

  if (state === 'error') return <StateCard role="alert" copy={copy.error} action={<RetryButton onRetry={onRetry} />} />;
  if (state === 'empty') return <StateCard copy={copy.empty} />;
  return children;
}
